import React, { useRef, useState, useEffect } from 'react';

import swal from 'sweetalert';

import CurrencyApi from '../../../Api/CRM/Modules/CurrencyApi';

import LotteryRulesApi from '../../../Api/CRM/Modules/LotteryRulesApi';
import { LotteryRule } from '../../../components/play/Types/Types';
import FormGroup from '../../elements/formElements/FormGroup';
import SubmitButton from '../../elements/formElements/SubmitButton';
import { roundNumber, roundPrice } from '../../helpers/formUtils';

const { default: ModalPopup } = require('../../elements/ModalPopup');

type Props = Readonly<{
    lotteryDisplaySlug: string;
    rules: LotteryRule;
}>;
function ChangeLinePriceModal({ lotteryDisplaySlug, rules }: Props) {
    const [isLoading, setIsLoading] = useState(true);
    const [currencyRate, setCurrencyRate] = useState<number>(1);
    const [lotteryRule, setLotteryRule] = useState<LotteryRule>({ ...rules });
    const [inputData, setInputData] = useState<{ fixedLineMarkup: string; linePriceSell: string }>({
        fixedLineMarkup: '',
        linePriceSell: '',
    });
    const [errors, setErrors] = useState({});
    const modalRef = useRef();

    const getErrorFor = (field: string) => {
        if (errors[field]) {
            return errors[field][0];
        }

        return null;
    };

    const filterInputData = (data: string): number => {
        const changedFloatedData = data.replace(',', '.');
        const floatRegExp = new RegExp(/^[+-]?([0-9]*[.])?[0-9]+/);
        if (floatRegExp.test(changedFloatedData) && !Number.isNaN(+changedFloatedData)) {
            if (+changedFloatedData >= 99999) {
                return 99999;
            }

            if (+changedFloatedData <= -99999) {
                return -99999;
            }

            return +changedFloatedData;
        }

        return 0;
    };
    const actualizePrices = () => {
        const baseFixedLineMarkup = Number(inputData.fixedLineMarkup);
        const baseLinePriceSell = Number(inputData.linePriceSell);

        const baseCurrencyPrices = {
            ...lotteryRule.base_currency_prices,
            fixed_line_markup: baseFixedLineMarkup,
            line_sell_price: baseLinePriceSell,
        };

        const exchangedCurrencyPrices = {
            ...lotteryRule.exchange_currency_prices,
            fixed_line_markup: baseFixedLineMarkup !== 0 ? roundPrice(baseFixedLineMarkup * currencyRate) : 0,
            line_sell_price: baseLinePriceSell !== 0 ? roundPrice(baseLinePriceSell * currencyRate) : 0,
            line_buy_price: roundPrice(lotteryRule.base_currency_prices.line_buy_price * currencyRate),
            fixed_line_provider_fee: roundPrice(
                lotteryRule.base_currency_prices.fixed_line_provider_fee * currencyRate
            ),
        };

        setLotteryRule({
            ...lotteryRule,
            base_currency_prices: baseCurrencyPrices,
            exchange_currency_prices: exchangedCurrencyPrices,
            line_sell_price: exchangedCurrencyPrices.line_sell_price,
            fixed_line_markup: baseCurrencyPrices.fixed_line_markup,
            fixed_line_markup_exchanged: exchangedCurrencyPrices.fixed_line_markup,
        });
    };

    const calculateLineSellPrice = (fixedLineMarkup: number) => {
        const linePriceSell = roundNumber(fixedLineMarkup + lotteryRule.base_currency_prices.line_buy_price, 2);

        setInputData({
            fixedLineMarkup: fixedLineMarkup.toString(),
            linePriceSell: linePriceSell.toString(),
        });
    };

    const calculateFixedLineMarkup = (linePriceSell: number) => {
        const fixedLineMarkup = roundNumber(linePriceSell - lotteryRule.base_currency_prices.line_buy_price, 2);

        setInputData({
            fixedLineMarkup: fixedLineMarkup.toString(),
            linePriceSell: linePriceSell.toString(),
        });
    };

    useEffect(() => {
        setInputData({
            fixedLineMarkup: lotteryRule.base_currency_prices.fixed_line_markup.toString(),
            linePriceSell: lotteryRule.base_currency_prices.line_sell_price.toString(),
        });

        if (lotteryRule.line_buy_currency_code !== lotteryRule.line_sell_currency_code) {
            CurrencyApi.getRate(lotteryRule.line_buy_currency_code, lotteryRule.line_sell_currency_code).then(
                (response) => {
                    setCurrencyRate(Number(response));
                    setIsLoading(false);
                }
            );
        } else {
            setIsLoading(false);
        }
    }, []);

    useEffect(() => {
        actualizePrices();
    }, [inputData, currencyRate]);

    const handleSubmit = (e) => {
        e.preventDefault();
        setIsLoading(true);

        LotteryRulesApi.updateLotteryRule(lotteryDisplaySlug, lotteryRule)
            .then((response) => {
                if (response) {
                    swal({ icon: 'success', text: 'Successful update line price' }).then(() => {
                        window.location.reload();
                    });
                }
                setIsLoading(false);
            })
            .catch((error) => {
                if (error.response) {
                    setErrors(error.response.data.errors);
                } else {
                    setErrors(error.message);
                }

                setIsLoading(false);
            });
    };

    function getContent() {
        return (
            <form onSubmit={handleSubmit} autoComplete="do-not-autofill">
                <FormGroup
                    type="text"
                    disableAutofill
                    error={getErrorFor('line_buy_price')}
                    handleChange={() => {}}
                    label="Line buy price"
                    value={`${lotteryRule.base_currency_prices.line_buy_price || 0} ${
                        lotteryRule.base_currency_prices.currency_code
                    }`}
                    field="line_buy_price"
                    disabled
                />
                <FormGroup
                    type="text"
                    disableAutofill
                    error={getErrorFor('fixed_line_provider_fee')}
                    handleChange={() => {}}
                    label={`Fixed provider line fee (${lotteryRule.base_currency_prices.currency_code}) included in above price`}
                    value={lotteryRule.base_currency_prices.fixed_line_provider_fee || 0}
                    field="fixed_line_provider_fee"
                    disabled
                />
                <FormGroup
                    type="text"
                    field="fixed_line_markup"
                    value={inputData.fixedLineMarkup || 0}
                    label={`Fixed line markup (${lotteryRule.base_currency_prices.currency_code})`}
                    onBlur={() => calculateLineSellPrice(filterInputData(inputData.fixedLineMarkup))}
                    handleChange={(e) => setInputData({ ...inputData, fixedLineMarkup: e.target.value })}
                    disableAutofill
                    error={getErrorFor('fixed_line_markup')}
                />

                <FormGroup
                    type="text"
                    disableAutofill
                    error={getErrorFor('base_line_sell_price')}
                    onBlur={() => calculateFixedLineMarkup(filterInputData(inputData.linePriceSell))}
                    handleChange={(e) => setInputData({ ...inputData, linePriceSell: e.target.value })}
                    label={`Line price sell (${lotteryRule.base_currency_prices.currency_code})`}
                    value={inputData.linePriceSell || 0}
                    field="base_line_sell_price"
                />

                <div className="pt-5">
                    <h4 className="underline">
                        <strong>Price summary</strong>
                    </h4>
                    <p>
                        <strong>Ticket line price</strong> {lotteryRule.exchange_currency_prices.line_buy_price}{' '}
                        {lotteryRule.exchange_currency_prices.currency_code}
                    </p>
                    <p>
                        <strong>Fixed line markup</strong> {lotteryRule.exchange_currency_prices.fixed_line_markup}{' '}
                        {lotteryRule.exchange_currency_prices.currency_code}
                    </p>
                    <p>
                        <strong>Final price</strong> {lotteryRule.exchange_currency_prices.line_sell_price}{' '}
                        {lotteryRule.exchange_currency_prices.currency_code}
                    </p>
                </div>

                <SubmitButton label="Update price" isLoading={isLoading} />
            </form>
        );
    }

    function getContentLoading() {
        return (
            <div className="text-center">
                <div className="spinner-grow spinner-grow-sm ml-auto" role="status">
                    <span className="sr-only">Loading...</span>
                </div>
            </div>
        );
    }

    return (
        <ModalPopup
            ref={modalRef}
            modalTitle="Change line price"
            modalContent={isLoading ? getContentLoading() : getContent()}
            buttonContent="Change line price"
            buttonClass="btn-primary ml-2"
        />
    );
}

export default ChangeLinePriceModal;
